(function () {
  const config = window.TikooDirectCheckoutConfig || {};
  const checkoutButtonSelector = 'button[name="checkout"]';
  const checkoutSummarySelector = '[data-tikoo-direct-checkout-summary]';
  let checkoutInProgress = false;
  let checkoutPolicyPromise = null;
  let checkoutState = { canCheckout: false };

  function readPositiveInteger(value) {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }

  function normalizeText(value) {
    if (typeof value !== 'string') return null;
    const normalized = value.trim();
    return normalized.length > 0 ? normalized : null;
  }

  function getApiBaseUrl() {
    return (normalizeText(config.proxyBasePath) || normalizeText(config.apiBaseUrl))?.replace(/\/+$/, '') || null;
  }

  function buildApiUrls(apiBaseUrl, action) {
    const paths = apiBaseUrl.startsWith('/apps/')
      ? [`/${action}`, `/api/shopify/${action}`]
      : [`/api/shopify/${action}`];
    return [...new Set(paths)].map((path) => `${apiBaseUrl}${path}`);
  }

  async function getCheckoutPolicy() {
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) throw new Error('Checkout policy is temporarily unavailable.');

    let lastError = null;
    for (const url of buildApiUrls(apiBaseUrl, 'direct-checkout-policy')) {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      const body = await response.json().catch(() => null);
      if (response.ok && body?.success !== false) {
        const policy = body?.data ?? body;
        const minimumItemCount = readPositiveInteger(policy?.minimumItemCount);
        const shippingCentsPerItem = readPositiveInteger(policy?.shippingCentsPerItem);
        if (minimumItemCount && shippingCentsPerItem) {
          return { minimumItemCount, shippingCentsPerItem };
        }
        throw new Error('Checkout policy is unavailable. Please refresh and try again.');
      }

      lastError = new Error(
        body?.message || body?.error?.message || `Checkout policy request failed with status ${response.status}.`
      );
      if (response.status !== 404) break;
    }

    throw lastError || new Error('Checkout policy is temporarily unavailable.');
  }

  function loadCheckoutPolicy() {
    if (!checkoutPolicyPromise) {
      checkoutPolicyPromise = getCheckoutPolicy().catch((error) => {
        checkoutPolicyPromise = null;
        throw error;
      });
    }

    return checkoutPolicyPromise;
  }

  function formatMoney(amountCents, currencyCode) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currencyCode || 'USD',
    }).format(amountCents / 100);
  }

  function setCheckoutButtonAvailability(canCheckout) {
    checkoutState = { canCheckout };
    document.querySelectorAll(checkoutButtonSelector).forEach((button) => {
      button.disabled = !canCheckout || checkoutInProgress;
      button.setAttribute('aria-disabled', String(!canCheckout || checkoutInProgress));
    });
  }

  function updateCheckoutSummary(cart, policy) {
    const itemCount = Number(cart.item_count || 0);
    const shippingAmountCents = itemCount * policy.shippingCentsPerItem;
    const totalAmountCents = Number(cart.total_price || 0) + shippingAmountCents;
    const currencyCode = cart.currency || 'USD';
    const canCheckout = itemCount >= policy.minimumItemCount;

    document.querySelectorAll('[data-tikoo-checkout-shipping]').forEach((element) => {
      element.textContent = formatMoney(shippingAmountCents, currencyCode);
    });
    document.querySelectorAll('[data-tikoo-checkout-total]').forEach((element) => {
      element.textContent = formatMoney(totalAmountCents, currencyCode);
    });
    document.querySelectorAll('[data-tikoo-checkout-shipping-note]').forEach((element) => {
      element.textContent = `Shipping is ${formatMoney(policy.shippingCentsPerItem, currencyCode)} per piece.`;
    });
    document.querySelectorAll('[data-tikoo-minimum-order-message]').forEach((element) => {
      if (canCheckout) {
        element.hidden = true;
        return;
      }

      element.hidden = false;
      element.textContent = `Add ${policy.minimumItemCount - itemCount} more pieces to place your order. Minimum order quantity is ${policy.minimumItemCount} pieces.`;
    });
    setCheckoutButtonAvailability(canCheckout);
  }

  function showCheckoutPolicyUnavailable(message) {
    document.querySelectorAll('[data-tikoo-checkout-shipping], [data-tikoo-checkout-total]').forEach((element) => {
      element.textContent = 'Unavailable';
    });
    document.querySelectorAll('[data-tikoo-checkout-shipping-note]').forEach((element) => {
      element.textContent = 'Checkout terms are temporarily unavailable.';
    });
    document.querySelectorAll('[data-tikoo-minimum-order-message]').forEach((element) => {
      element.hidden = false;
      element.textContent = 'Checkout terms are temporarily unavailable. Please refresh and try again.';
    });
    setCheckoutButtonAvailability(false);
    setCheckoutError(message);
  }

  async function refreshCheckoutSummary(cartData) {
    if (!document.querySelector(checkoutSummarySelector)) return;

    try {
      const [policy, cart] = await Promise.all([loadCheckoutPolicy(), cartData || readCart()]);
      updateCheckoutSummary(cart, policy);
    } catch (error) {
      showCheckoutPolicyUnavailable(
        error instanceof Error ? error.message : 'Checkout terms are temporarily unavailable.'
      );
    }
  }

  function setCheckoutError(message) {
    const errorElement = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
    if (!errorElement) return;
    errorElement.textContent = message;
    errorElement.setAttribute('role', 'alert');
  }

  function setButtonsBusy(isBusy) {
    document.querySelectorAll(checkoutButtonSelector).forEach((button) => {
      if (!button.dataset.tikooCheckoutLabel) {
        button.dataset.tikooCheckoutLabel = button.textContent.trim();
      }
      button.disabled = isBusy || !checkoutState.canCheckout;
      button.setAttribute('aria-disabled', String(isBusy || !checkoutState.canCheckout));
      button.textContent = isBusy ? 'Preparing checkout...' : button.dataset.tikooCheckoutLabel;
    });
  }

  async function readCart() {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) throw new Error('Your cart could not be read. Please refresh and try again.');
    return response.json();
  }

  async function createCheckout(cart) {
    const apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) throw new Error('Checkout is temporarily unavailable.');

    const payload = {
      items: cart.items.map((item) => ({
        variantId: String(item.variant_id || item.id || ''),
        productId: item.product_id ? String(item.product_id) : null,
        quantity: Number(item.quantity || 0),
      })),
    };
    let lastError = null;
    for (const url of buildApiUrls(apiBaseUrl, 'direct-checkout')) {
      const response = await fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      const body = await response.json().catch(() => null);
      if (response.ok && body?.success !== false) return body?.data ?? body;

      lastError = new Error(
        body?.message || body?.error?.message || `Checkout request failed with status ${response.status}.`
      );
      if (response.status !== 404) break;
    }

    throw lastError || new Error('Checkout could not be created.');
  }

  async function beginCheckout() {
    if (checkoutInProgress) return;
    checkoutInProgress = true;
    setCheckoutError('');
    setButtonsBusy(true);
    try {
      const policy = await loadCheckoutPolicy();
      const cart = await readCart();
      const itemCount = Number(cart.item_count || 0);
      if (itemCount < policy.minimumItemCount) {
        throw new Error(`Add ${policy.minimumItemCount - itemCount} more pieces before checkout.`);
      }

      const result = await createCheckout(cart);
      const checkoutUrl = normalizeText(result?.checkoutUrl);
      if (!checkoutUrl) throw new Error('Shopify did not return a checkout URL. Please try again.');
      const estimatedTotalCents = Number(cart.total_price || 0) + itemCount * policy.shippingCentsPerItem;
      const authoritativeTotalCents = Number(result?.totalAmountCents);
      if (Number.isFinite(authoritativeTotalCents) && authoritativeTotalCents !== estimatedTotalCents) {
        const authoritativeTotal = `$${(authoritativeTotalCents / 100).toFixed(2)}`;
        const confirmed = window.confirm(
          `The listed price changed while your cart was open. Your current checkout total is ${authoritativeTotal}. Continue to payment?`
        );
        if (!confirmed) {
          throw new Error('Checkout was not opened because the current price was not confirmed.');
        }
      }
      window.location.assign(checkoutUrl);
    } catch (error) {
      setCheckoutError(error instanceof Error ? error.message : 'Checkout could not be created.');
      checkoutInProgress = false;
      setButtonsBusy(false);
    }
  }

  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    if (!(submitter instanceof HTMLButtonElement) || submitter.name !== 'checkout') return;

    event.preventDefault();
    void beginCheckout();
  });

  if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
    subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => refreshCheckoutSummary(event?.cartData));
  }

  const checkoutSummaryObserver = new MutationObserver((mutations) => {
    const cartSummaryWasRendered = mutations.some((mutation) =>
      [...mutation.addedNodes].some((node) =>
        node instanceof Element &&
        (node.matches(checkoutSummarySelector) || node.querySelector(checkoutSummarySelector))
      )
    );
    if (cartSummaryWasRendered) {
      void refreshCheckoutSummary();
    }
  });

  checkoutSummaryObserver.observe(document.body, { childList: true, subtree: true });
  void refreshCheckoutSummary();
})();
